/**
 * Global-skill sync planning (ADR-0023 / ENG-6349) — the pure decision logic
 * behind the manager's per-refresh global-skill install, split out so it can be
 * unit-tested without the manager's IO/adapter/fs surface.
 *
 * Global skills (`scope=global`) are delivered to EVERY agent install-free via
 * `/host/refresh.global_skills`. The manager writes each to
 * `.claude/skills/<code_name>/SKILL.md` (bare code_name so the agent's
 * `/<code_name>` resolves), skips unchanged ones by content hash, and removes
 * the folder for any global it previously installed that's no longer published.
 */

export interface GlobalSkillInput {
  code_name?: string;
  display_name?: string;
  content?: string;
}

export interface GlobalSkillPlan {
  /** Skills to (re)write — new or content-changed since last refresh. */
  installs: Array<{ skillId: string; content: string; hash: string }>;
  /** Previously-installed global skill ids no longer published → remove folder. */
  removes: string[];
  /** The full set of valid global skill ids this refresh (for next-refresh diff). */
  currentIds: Set<string>;
}

/** Folder-safe id from a skill code_name (kebab, matching the skills dir convention). */
export function sanitizeGlobalSkillId(codeName: string): string {
  return codeName
    .trim()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Compute what to install and what to remove for this agent's global skills.
 * Pure: callers supply the content hasher and the last-known-hash lookup, and
 * perform the actual installSkillFiles / rmSync IO against the returned plan.
 */
export function planGlobalSkillSync(
  globalSkills: GlobalSkillInput[],
  prevIds: Set<string>,
  hashOf: (content: string) => string,
  knownHash: (skillId: string) => string | undefined,
): GlobalSkillPlan {
  const currentIds = new Set<string>();
  const installs: GlobalSkillPlan['installs'] = [];

  for (const gs of globalSkills ?? []) {
    const codeName = (gs?.code_name ?? '').trim();
    const content = gs?.content ?? '';
    // A global skill needs a code_name and a non-empty body to produce a SKILL.md.
    if (!codeName || !content.trim()) continue;
    const skillId = sanitizeGlobalSkillId(codeName);
    if (!skillId) continue;
    currentIds.add(skillId);
    const hash = hashOf(content);
    if (knownHash(skillId) === hash) continue; // unchanged → skip the disk write
    installs.push({ skillId, content, hash });
  }

  const removes = [...prevIds].filter((id) => !currentIds.has(id));
  return { installs, removes, currentIds };
}
